import { useQueryClient } from "@tanstack/react-query";
import type { FundingApplicationSchemas } from "api-types";
import { useParams } from "react-router";
import { ConnectionAuthCallbackHandler, getInReviewDetailsQueryKey, useStrictParams } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { useAirBackNavigation } from "../../hooks/air-back-navigation";
import { useAIRDetails } from "../../queries/review-screen/review-screen-queries";

import { AirBankStatementsPage, RevenueSourcePage } from "./air-pages";
import { ReconnectConnectionPage } from "./air-pages/reconnect-connection";

const getAirComponent = (customerDomain: string, onBack: () => void, air?: FundingApplicationSchemas.AIRSchema) => {
  if (!air || !air.air_data) {
    return null;
  }
  switch (air.air_data.type) {
    case "bank_statements_for_account":
      return <AirBankStatementsPage customerDomain={customerDomain} air={air} onBack={onBack} />;
    case "additional_revenue_source":
      return <RevenueSourcePage air={air} onBack={onBack} />;
    case "reconnect_connection":
      return <ReconnectConnectionPage air={air} onBack={onBack} />;
    default:
      // Air type not supported by UI
      null;
  }
};

export const AdditionalInformationRequestPage = () => {
  const { customerDomain } = useStrictParams<{ customerDomain: string }>();
  const queryClient = useQueryClient();

  return (
    <ConnectionAuthCallbackHandler
      onSuccess={async () => {
        await queryClient.invalidateQueries({
          queryKey: getInReviewDetailsQueryKey(customerDomain),
        });
      }}
    >
      <AirContent />
    </ConnectionAuthCallbackHandler>
  );
};

function AirContent() {
  const onBack = useAirBackNavigation();
  const { airId } = useParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const { data: air, isFetchedAfterMount } = useAIRDetails(customerDomain, airId ?? "");
  const airComponent = getAirComponent(customerDomain, onBack, air);

  const shouldNavigateToReview = isFetchedAfterMount && (!air || !air.air_data || !air.can_access || !airComponent);

  if (shouldNavigateToReview) {
    onBack();
  }

  return isFetchedAfterMount ? airComponent : null;
}
